import { Stack, Typography } from '@mui/material';
import React, { cloneElement, isValidElement } from 'react';
import { useNavigate } from 'react-router';

import { OpenIcon } from '@/shared/assets/icons/open-icon';
import { FeatureCardContainer } from '@/shared/components/ui/feature-card/feature-card.styles';
import {
  TFeatureCardProps,
  TFeatureCardVariant,
} from '@/shared/components/ui/feature-card/feature-card.types';
import { NavigatableLink } from '@/shared/components/ui/navigatable-link/navigatable-link';
import { useHasPermission } from '@/shared/features/permissions/hooks/use-permission-check/use-permission-check';

export const FeatureCard: React.FC<TFeatureCardProps> = ({
  icon,
  title,
  titleAdornment,
  description,
  navigateTo,
  variant,
  permission,
  visible = true,
}) => {
  const navigate = useNavigate();
  const hasPermission = useHasPermission(permission);

  if (!visible) {
    return null;
  }

  const renderIcon = () => {
    if (!isValidElement<React.HTMLAttributes<HTMLElement>>(icon)) {
      return icon;
    }
    return cloneElement(icon, { className: 'feature-card-icon' });
  };

  const renderDescription = () => {
    if (!description) {
      return null;
    }

    if (typeof description === 'string') {
      return (
        <Typography variant="p8" color="textSecondary">
          {description}
        </Typography>
      );
    }

    return description;
  };

  const renderCardContent = () => (
    <>
      {renderIcon()}
      <Stack gap="0.75rem" flex={1}>
        <Stack direction="row" gap="0.5rem" justifyContent="space-between">
          <Typography
            variant={variant === TFeatureCardVariant.SMALL ? 'p1' : 'h6'}
          >
            {title}
          </Typography>
          {titleAdornment}
        </Stack>
        {renderDescription()}
      </Stack>
    </>
  );

  const onClick = navigateTo ? () => navigate(navigateTo) : undefined;

  if (!navigateTo) {
    return (
      <FeatureCardContainer $variant={variant}>
        {renderCardContent()}
      </FeatureCardContainer>
    );
  }

  if (!hasPermission) {
    return null;
  }

  return (
    <NavigatableLink to={navigateTo}>
      <FeatureCardContainer onClick={onClick} $variant={variant}>
        {renderCardContent()}
        <OpenIcon variant="light" />
      </FeatureCardContainer>
    </NavigatableLink>
  );
};
